import { useEffect, useState } from "react"
import { DateTime } from "luxon"

import { SelectedPriceListItem } from "../../../createOrder/createOrderTypes"
import {
    DeliveryTimesField,
    DynamicConfigKey,
    isProductWishOrderLine,
    MeetingServiceTabValues,
} from "../../components/form/formTypes"
import { ReceiptConfirmOrderDialogWrap } from "./ReceiptConfirmOrderDialogWrap"
import { OrderFormStatus } from "../../mainForm/helpers"
import { useCreateOrderForm } from "../../mainForm/CreateOrderFormContext"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { DeadlineMessageDialog } from "./DeadlineMessageDialog"
import {
    DeadlineMessageQuery,
    DeadlineMessageResponse,
    fetchDeadlineMessage,
} from "../../components/shop/delivery/deliveryApi"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { CreateOrderType } from "../../helpers"
import { UserType } from "../../../../../modules/login/loginTypes"

const transformData = (data: DeadlineMessageResponse) => {
    return data.deadlineMessage
}

type ReceiptConfirmOrderDialogControllerProps = Readonly<{
    onClose: () => void
    orderDetails: MeetingServiceTabValues
    handleOpenResourceBooking: (type: CreateOrderType) => void
    handleAddNewMeetingService: () => void
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export function ReceiptConfirmOrderDialogController({
    onClose,
    orderDetails,
    handleOpenResourceBooking,
    handleAddNewMeetingService,
    orderFormStatusRef,
}: ReceiptConfirmOrderDialogControllerProps) {
    const { user, handleClose, orderContext, setOrderContext, availableModules } = useCreateOrderForm()
    const [showReceiptDialog, setShowReceiptDialog] = useState(false)
    const { isSpecialOrder } = useCreateOrderDialogType()

    const multipleDeliveries: DeliveryTimesField = orderDetails[DynamicConfigKey.MULTIPLE_DELIVERIES]!
    const deliveryDate = multipleDeliveries && multipleDeliveries.deliveryDate
    const deliveryTime = DateTime.fromISO(`${deliveryDate}T${multipleDeliveries.deliveryTimes[0].time}`).toISO({
        suppressSeconds: true,
        includeOffset: false,
    })

    const priceList: SelectedPriceListItem = orderDetails["priceList"].item!

    const hasProductWish =
        multipleDeliveries?.deliveryTimes?.some((delivery) =>
            delivery.orderLines?.some((line) => isProductWishOrderLine(line)),
        ) ?? false

    // Staff/merchant (on-behalf) ordering: show the product-wish message from their POV instead of the customer's.
    const isStaffProductWish = hasProductWish && user.userType !== UserType.USER

    const { data: messageInfo, isLoading } = useApiQuery(fetchDeadlineMessage, {
        queryName: DeadlineMessageQuery.DELIVERY_DEADLINE_MESSAGE,
        dependencies: {
            merchantId: priceList.merchantId,
            deliveryTime,
            productWish: hasProductWish,
            priceListId: priceList.priceListId,
            shopId: priceList.shopId,
        },
        select: transformData,
        enabled: !!deliveryDate && !isSpecialOrder,
    })

    useEffect(() => {
        if (isSpecialOrder) {
            // For special dialogs, skip deadline message check and show receipt dialog directly
            setShowReceiptDialog(true)
        } else if (!isLoading && messageInfo) {
            if (messageInfo.isConfirmed) {
                setShowReceiptDialog(true)
            }
        }
    }, [isLoading, messageInfo, isSpecialOrder])

    // Show receipt dialog when ready
    if (showReceiptDialog) {
        return (
            <ReceiptConfirmOrderDialogWrap
                orderContext={orderContext}
                setOrderContext={setOrderContext}
                open={showReceiptDialog}
                onClose={onClose}
                handleCloseMainDialog={handleClose}
                handleOpenResourceBooking={handleOpenResourceBooking}
                handleAddNewMeetingService={handleAddNewMeetingService}
                orderDetails={orderDetails}
                mainUser={user}
                orderFormStatusRef={orderFormStatusRef}
                availableModules={availableModules}
            />
        )
    }

    // For special dialogs, don't wait for deadline message (already handled in useEffect)
    if (isSpecialOrder) {
        return null
    }

    // For regular dialogs, wait for deadline message
    if (isLoading || !messageInfo) {
        return null
    }

    if (!messageInfo.isConfirmed) {
        return (
            <DeadlineMessageDialog
                message={messageInfo.message}
                isStaffProductWish={isStaffProductWish}
                onClose={onClose}
                handlePlaceOrder={() => setShowReceiptDialog(true)}
            />
        )
    }

    return null
}
